import { kindLoadsTruck, kindUnloadsTruck, kindIsPackOnly } from '../lib/phaseKinds';

export function getTripTag(tripTags, dateStr) {
    const v = (tripTags || {})[dateStr];
    if (!v) return null;
    if (typeof v === 'string') return v;
    return v.tag || null;
}

export function getLegCrew(tripTags, dateStr) {
    const v = (tripTags || {})[dateStr];
    if (!v || typeof v === 'string') return null;
    return v.leg_crew_ids || null;
}

export function setLegCrewInTags(tripTags, dateStr, crewIds) {
    const existing = (tripTags || {})[dateStr];
    const tag = typeof existing === 'string' ? existing : (existing?.tag || 'Drive');
    return { ...tripTags, [dateStr]: { tag, leg_crew_ids: crewIds } };
}

const MOVING_SKILL_TITLES = { 1: 'New Recruit', 2: 'Rookie', 3: 'Seasoned Crew', 4: 'Jr Crew Leader', 5: 'Crew Leader' };

export function getMovingTitle(lvl) {
    return MOVING_SKILL_TITLES[Number(lvl)] || `Moving ${lvl}`;
}

export function getCrewSkillSummary(member) {
    const skills = [];
    if ((member.skill_moving || 0) > 0) skills.push(getMovingTitle(member.skill_moving));
    if ((member.skill_packing || 0) > 0) skills.push(`Packing Lvl ${member.skill_packing}`);
    return skills.length > 0 ? skills.join(' · ') : 'No skills set';
}

export function getTopSkillLevel(member) {
    return Math.max(member.skill_moving || 0, member.skill_packing || 0, member.skill_level || 1);
}

// ── Transfer truck (sql/081, Jaylen 2026-08-10) ─────────────────────────
// A small shuttle truck needed on specific LEGS only (steep driveway /
// tight access at one end). estimates.transfer_truck: null | 'load' |
// 'unload' | 'both'. This predicate answers "does THIS leg need the
// shuttle?" — used by the requirements math below and by the dispatch
// board's truck cascade (covered legs are leg-local: their truck edits
// don't chain through the trip).
// Coverage sides derive from the kind's SEGMENTS (phaseKinds.js, 2026-08-26
// combo refactor) — a leg is load-side if any segment loads the truck,
// unload-side if any segment unloads it. Replaces the hardcoded name lists.
export function transferTruckCovers(estimate, phaseKind) {
    const t = estimate?.transfer_truck;
    if (!t) return false;
    const kind = phaseKind || 'move';
    if (t === 'load') return kindLoadsTruck(kind);
    if (t === 'unload') return kindUnloadsTruck(kind);
    if (t === 'both') return kindLoadsTruck(kind) || kindUnloadsTruck(kind);
    return false;
}

// Truck size label → assigned-truck count it represents (2x26ft = 2 trucks).
const TRUCK_REQ_COUNT = { 'none': 0, '16ft': 1, '20ft': 1, '26ft': 1, '2x26ft': 2 }; // 20ft added 2026-09-11 (was counting as 0 required); 2x26ft kept for legacy rows

// jobResourceStatus — compare a dispatch job's ASSIGNED crew/trucks against
// the REQUIREMENTS on its linked estimate. Used for the requirement
// checkmarks on the expanded pill and the non-blocking pre-send warning.
// Returns counts, per-resource met flags, allMet, and a human "missing" list.
//
// `trucks` (optional third arg) is the fleet list from the store — needed
// to resolve the packing_vehicle flag on PACK legs. Callers that can't
// supply it fall back to "any truck assigned counts".
export function jobResourceStatus(job, estimate, trucks = null) {
    // ── PACK legs (Jensen 2026-08-25) ─────────────────────────────────
    // A packing day doesn't need the whole-move allocation (26' + extras
    // + full move crew). Its requirements are: the PACKING crew (the
    // phase's own crew_size, falling back to the estimate's packing
    // crew) and ONE packing vehicle — any truck flagged packing_vehicle
    // (sql/082: the 16-footers + the pack van).
    // Pack-ONLY days (segments === ['pack']) — pack_load and other
    // combos containing a load keep the whole-move allocation.
    if (kindIsPackOnly(job.phase_kind) && job.phase_kind) {
        const requiredCrew = Number(job.crew_size) || Number(estimate?.packing_crew_size) || 0;
        const assignedCrew = (job.crew_ids || []).length;
        const crewMet = assignedCrew >= requiredCrew;
        const assignedTrucks = (job.truck_ids || []).length;
        const packVehicleMet = Array.isArray(trucks)
            ? (job.truck_ids || []).some(id => trucks.find(t => t.id === id)?.packing_vehicle)
            : assignedTrucks >= 1;
        const missing = [];
        if (!packVehicleMet) missing.push(`packing vehicle (${assignedTrucks > 0 ? 'assigned truck isn’t flagged as one' : '0/1'})`);
        if (requiredCrew > 0 && !crewMet) missing.push(`packers (${assignedCrew}/${requiredCrew})`);
        return {
            requiredTrucks: 1, assignedTrucks, trucksMet: packVehicleMet,
            transferRequired: false, packingVehicleRequired: true,
            requiredCrew, assignedCrew, crewMet,
            allMet: packVehicleMet && (requiredCrew === 0 || crewMet),
            missing,
        };
    }

    let requiredTrucks = 0;
    if (estimate) {
        const all = [estimate.trucks_needed];
        try {
            const arr = JSON.parse(estimate.extra_trucks || '[]');
            if (Array.isArray(arr)) all.push(...arr);
        } catch { /* ignore */ }
        requiredTrucks = all.reduce((n, t) => n + (TRUCK_REQ_COUNT[t] ?? (t && t !== 'none' ? 1 : 0)), 0);
    }
    // Transfer truck (sql/081): covered legs need one extra (small) truck
    // on top of the trip's trucks — the shuttle for the tight end.
    const transferRequired = transferTruckCovers(estimate, job.phase_kind);
    if (transferRequired) requiredTrucks += 1;
    const assignedTrucks = (job.truck_ids || []).length;
    const trucksMet = assignedTrucks >= requiredTrucks;

    // crew_size_needed is the primary mover count (local) / load crew (OOS).
    const requiredCrew = Number(estimate?.crew_size_needed) || 0;
    const assignedCrew = (job.crew_ids || []).length;
    const crewMet = assignedCrew >= requiredCrew;

    const missing = [];
    if (requiredTrucks > 0 && !trucksMet) missing.push(`trucks (${assignedTrucks}/${requiredTrucks}${transferRequired ? ' incl. transfer' : ''})`);
    if (requiredCrew > 0 && !crewMet) missing.push(`crew (${assignedCrew}/${requiredCrew})`);

    return {
        requiredTrucks, assignedTrucks, trucksMet, transferRequired,
        packingVehicleRequired: false,
        requiredCrew, assignedCrew, crewMet,
        allMet: (requiredTrucks === 0 || trucksMet) && (requiredCrew === 0 || crewMet),
        missing,
    };
}
